// Helper to aggregate donut chart data based on metric type and orders data

type Order = { mission_advancement_id: number; count: number };
type rating = '5' | '4' | '3' | '2' | '1' | '0' | 'S' | 99;

interface DonutAggregationResult {
  donutDataConsultant: Record<string, number>;
  donutDataDM: Record<string, number>;
  donutDataClient: Record<string, number>;
  missionByCustomer: Record<string, number>;
  missionByBM: Record<string, number>;
  missionData: Record<string, number>;
}

interface RatingDetail {
  rating: rating;
  count: number;
}

interface GraphDataOrders {
  consultant_submission?: Order[];
  dm_validation?: Order[];
  client_acceptance?: Order[];
  rating_details?: RatingDetail[];
  coverRatioData?: Order[];
  otpCustomerData?: Order[];
  otpBMData?: Order[];
}

// Status mapper function
type Context = 'consultant' | 'dm' | 'client';

const STATUS_MAP: Record<number, Partial<Record<Context, string>>> = {
  1: {
    dm: 'To_Be_Validated', // 'TO VALIDATE BY DM',
    consultant: 'Submitted',
    client: 'Pending_Delivery', // 'PENDING DM VALIDATION',
  },
  2: {
    dm: 'Pending_Submission', // 'DM REFUSE',
    consultant: 'Not_Submitted',
    client: 'Pending_Delivery', // 'PENDING DM VALIDATION',
  },
  3: {
    dm: 'Validation_Complete', // 'DM VALIDATED',
    consultant: 'Submitted',
    client: 'To_Be_Accepted', // 'WAITING CLIENT ACCEPTANCE',
  },
  4: {
    dm: 'Validation_Complete', // 'DM VALIDATED WITH RESERVATION',
    consultant: 'Submitted',
    client: 'To_Be_Accepted', // 'WAITING CLIENT ACCEPTANCE',
  },
  5: {
    dm: 'Validation_Complete', // 'TO VALIDATE BY DM',
    consultant: 'Submitted',
    client: 'Acceptance_Complete', //  'CLIENT REFUSE',
  },
  6: {
    dm: 'Validation_Complete', //  'DM VALIDATED',
    consultant: 'Submitted',
    client: 'Acceptance_Complete', // 'CLIENT ACCEPTED WITH RESERVATION',
  },
  7: {
    dm: 'Validation_Complete', // 'DM VALIDATED',
    consultant: 'Submitted',
    client: 'Acceptance_Complete', // 'CLIENT ACCEPTED',
  },
  99: {
    dm: 'Pending_Submission',
    consultant: 'Not_Submitted',
    client: 'Pending_Delivery', // 'PENDING DM VALIDATION',
  },
};

const DEFAULT_STATUS: Record<number, string> = {
  2: 'DM REFUSE',
  3: 'DM VALIDATED',
  4: 'DM VALIDATED WITH RESERVATION',
  5: 'CLIENT REFUSE',
  6: 'CLIENT ACCEPTED WITH RESERVATION',
  7: 'CLIENT ACCEPTED',
  99: 'NOT SUBMITTED',
};

export function getStatus(id: number, context?: Context): string {
  if (context && STATUS_MAP[id]?.[context]) {
    return STATUS_MAP[id]![context]!;
  }
  return DEFAULT_STATUS[id] || '';
}

// Merge consultant data (combine ID 99 and 2)
function mergeConsultantData(list: Order[]): Order[] {
  if (!Array.isArray(list)) return [];

  let arr = [...list];
  const obj99 = arr.find((o) => o.mission_advancement_id === 99);
  const obj2 = arr.find((o) => o.mission_advancement_id === 2);

  if (obj99 && obj2) {
    const index99 = arr.findIndex((o) => o.mission_advancement_id === 99);
    if (index99 !== -1) {
      arr[index99] = { ...arr[index99], count: arr[index99].count + obj2.count };
    }
    arr = arr.filter((o) => o.mission_advancement_id !== 2);
  }
  return arr;
}

/**
 * Aggregate donut data for metric 1 (Workflow)
 */
function aggregateMetric1(orders: GraphDataOrders): DonutAggregationResult {
  const consultant = { Submitted: 0, Not_Submitted: 0 };
  const dm = {
    // 1.	To be validated by DM: 0,
    // 2.	Validation complete :0
    // 3.	Pending Submission: 0
    To_Be_Validated: 0,
    Validation_Complete: 0,
    Pending_Submission: 0,

    // 'DM REFUSE': 0,
    // 'TO VALIDATE BY DM': 0,
    // 'DM PENDING SUBMISSION': 0,
    // 'DM VALIDATED': 0,
    // 'DM VALIDATED WITH RESERVATION': 0,
  };
  const client = {
    To_Be_Accepted: 0,
    Acceptance_Complete: 0,
    Pending_Delivery: 0,
    // 'CLIENT REFUSE': 0,
    // 'WAITING CLIENT ACCEPTANCE': 0,
    // 'CLIENT ACCEPTED': 0,
    // 'PENDING DM VALIDATION': 0,
    // 'CLIENT ACCEPTED WITH RESERVATION': 0,

    //     1.	TO BE ACCEPTED
    // 2.	ACCEPTANCE COMPLETE (For ACL: include Accepted, Refused, Accepted with reserve)
    // 3.	PENDING DELIVERY (for ACL: To be submitted + To be Validated)
  };

  // Merge consultant data first
  const mergedConsultant = mergeConsultantData(orders.consultant_submission || []);

  // Process consultant data
  mergedConsultant.forEach((order) => {
    const status = getStatus(order.mission_advancement_id, 'consultant');
    if (status in consultant) {
      consultant[status as keyof typeof consultant] += order.count;
    }
  });

  // Process DM and Client data
  (['dm_validation', 'client_acceptance'] as const).forEach((category) => {
    const categoryData = orders[category];
    if (Array.isArray(categoryData)) {
      categoryData.forEach((order) => {
        const context = category === 'dm_validation' ? 'dm' : 'client';
        const status = getStatus(order.mission_advancement_id, context);

        if (category === 'dm_validation' && status in dm) {
          dm[status as keyof typeof dm] += order.count;
        } else if (category === 'client_acceptance' && status in client) {
          client[status as keyof typeof client] += order.count;
        }
      });
    }
  });

  return {
    donutDataConsultant: consultant,
    donutDataDM: dm,
    donutDataClient: client,
    missionByCustomer: {},
    missionByBM: {},
    missionData: {},
  };
}

/**
 * Aggregate donut data for metric 2 (Acceptance)
 * Maps mission_advancement_id to acceptance states
 */
function aggregateMetric2(orders: GraphDataOrders): DonutAggregationResult {
  // For metric 2, we adapt the structure to acceptance states

  const client = {
    Accepted: 0,
    Accepted_With_Reservation: 0,
    Refused: 0,
    To_Be_Done: 0,
  };

  // Process client acceptance data (primary data for metric 2)
  const clientData = orders.client_acceptance || [];
  clientData.forEach((order) => {
    switch (order.mission_advancement_id) {
      case 7: // Accepted
        client.Accepted += order.count;
        break;
      case 6: // Accepted with reservation
        client.Accepted_With_Reservation += order.count;
        break;
      case 5: // Rejected
        client.Refused += order.count;
        break;
      case 1:
      case 2:
      case 3: // Pending
      case 4:
      case 99:
        client.To_Be_Done += order.count;
        break;
      default:
        break;
    }
  });

  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: client,
    missionByCustomer: {},
    missionByBM: {},
    missionData: {},
  };
}

function aggregateMetric3(orders: GraphDataOrders): DonutAggregationResult {
  // For metric 3, aggregate by rating type
  const client: Record<string, number> = {
    '5': 0,
    '4': 0,
    '3': 0,
    '2': 0,
    '1': 0,
    '0': 0,
    S: 0,
    // 'Not Rated': 0,
  };
  const clientData = orders.rating_details || [];

  clientData.forEach((order: { rating: rating; count: number }) => {
    // if (order.rating === 99) {
    //   client['Not Rated'] += order.count;
    // } else
    if (['5', '4', '3', '2', '1', '0', 'S'].includes(order.rating as string)) {
      client[order.rating as string] += order.count;
    }
  });
  // console.log(client);

  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: client,
    missionByCustomer: {},
    missionByBM: {},
    missionData: {},
  };
}

function aggregateMetric4(orders: GraphDataOrders): DonutAggregationResult {
  return {
    donutDataConsultant: {},
    donutDataDM: {},
    donutDataClient: {},
    missionByCustomer: {},
    missionByBM: {},
    missionData: {},
  };
}

/**
 * Main aggregation function that routes to metric-specific aggregators
 */
export default function aggregateDonutData(orders: GraphDataOrders, metricId = 1): DonutAggregationResult {
  if (metricId === 2) {
    return aggregateMetric2(orders);
  }
  if (metricId === 3) {
    return aggregateMetric3(orders);
  }
  if (metricId === 4) {
    return aggregateMetric4(orders);
  }
  // Default to metric 1
  return aggregateMetric1(orders);
}
